import React, {
  useReducer,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DropResult } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import {
  QuestionMultiChoice,
  QuestionSingleChoice,
  ChoiceQuestionPayload,
} from '../../redux/types';
import SortableList from '../layout/SortableList';

type Props<T extends QuestionMultiChoice|QuestionSingleChoice> = {
  surveyId: number;
  sectionId?: number;
  question?: T;
  addQuestion?: (dq: Omit<T, 'id'>) => void;
  updateQuestion?: (dq: T) => void;
  onCancelEdit?: () => void;
  type: T['type'];
  disableHideAggregation?: boolean;
}

type PayloadItem = ChoiceQuestionPayload;

type SortOption = PayloadItem & { name: string; };

type questionActions = { type: 'updateText'; payload: string } | { type: 'addOption'; payload: string } |
{ type: 'updateOption'; payload:PayloadItem } | { type: 'deleteOption', payload: PayloadItem; } |
{ type: 'resetState'} | { type: 'reorderOptions', payload: PayloadItem[] } | { type: 'hideAggregation', payload: boolean };

const initialState: { text: string, payload?: PayloadItem[], hideAggregation?: boolean } = {
  text: '',
  payload: undefined,
};

const reducer = (state: typeof initialState, action: questionActions): typeof initialState => {
  switch (action.type) {
    case 'resetState':
      return initialState;
    case 'updateText':
      return {
        ...state,
        text: action.payload,
      };
    case 'addOption': {
      const newPayload = state.payload ? [...state.payload] : [];
      newPayload.push({
        id: Date.now(),
        text: action.payload,
      });
      return {
        ...state,
        payload: newPayload,
      };
    }
    case 'updateOption': {
      const newPayload = state.payload ? [...state.payload] : [];
      const updatedPayload = newPayload.map((e) => {
        if (e.id === action.payload.id) {
          return {
            id: e.id,
            text: action.payload.text,
          };
        }
        return e;
      });
      return {
        ...state,
        payload: updatedPayload,
      };
    }
    case 'deleteOption': {
      const newPayload = state.payload ? [...state.payload] : [];
      const filteredPayload = newPayload.filter((e) => e.id !== action.payload.id);
      return {
        ...state,
        payload: filteredPayload,
      };
    }
    case 'reorderOptions': {
      return {
        ...state,
        payload: action.payload,
      };
    }
    case 'hideAggregation': {
      return {
        ...state,
        hideAggregation: action.payload,
      };
    }
    default:
      return state;
  }
};

const ChoiceQuestionEditor = <
  T extends QuestionSingleChoice|QuestionMultiChoice,
>({
    surveyId,
    sectionId,
    question,
    addQuestion,
    updateQuestion,
    onCancelEdit,
    type,
    disableHideAggregation,
  }: Props<T>): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, question || initialState);
  const [optionText, setOptionText] = useState<string>('');
  const [sort, setSort] = useState<boolean>(false);
  const sortedListRef = useRef<SortOption[]>();
  const sortOptionList = state.payload?.map((e) => ({ ...e, name: e.text } as SortOption));

  const handleQuestionTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const questionText = event.target.value;
    dispatch({
      type: 'updateText',
      payload: questionText,
    });
  };

  const onChangeOptionHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optiontxt = event.target.value;
    setOptionText(optiontxt);
  };

  const handleEditOption = (text: string, item: PayloadItem) => {
    dispatch({
      type: 'updateOption',
      payload: {
        id: item.id,
        text,
      },
    });
  };
  const handleAddOption = () => {
    dispatch({
      type: 'addOption',
      payload: optionText,
    });
    setOptionText('');
  };

  const onEnterAddOption = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddOption();
    }
  };

  const handleDeleteOption = (item: PayloadItem) => {
    dispatch({
      type: 'deleteOption',
      payload: item,
    });
  };

  const saveMultiQuestion = () => {
    if (question && updateQuestion) {
      const updatedQuestion = {
        ...question,
        text: state.text,
        payload: state.payload,
        hideAggregation: state.hideAggregation,
      };
      updateQuestion(updatedQuestion);
      if (onCancelEdit) {
        onCancelEdit();
      }
    } else if (!question && addQuestion) {
      const questionData = {
        digitalSurveyId: surveyId,
        digitalSectionId: sectionId,
        type,
        text: state.text,
        payload: state.payload,
        hideAggregation: state.hideAggregation,
      } as Omit<T, 'id'>;
      addQuestion(questionData);
      dispatch({ type: 'resetState' });
    }
  };

  const cancelEdit = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const sortHandler = () => {
    setSort(true);
  };

  const saveSortHandler = () => {
    setSort(false);
    if (sortedListRef.current) {
      dispatch({
        type: 'reorderOptions',
        payload: sortedListRef.current,
      });
    }
    sortedListRef.current = undefined;
  };

  const onDragHandler = useCallback((result: DropResult, sortedList?: SortOption[]) => {
    if (sortedList) {
      sortedListRef.current = sortedList;
    }
  }, []);

  const onHideFromAggregation = (e: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    dispatch({
      type: 'hideAggregation',
      payload: checked,
    });
  };

  return (
    <Box>
      <TextField
        variant="filled"
        size="small"
        label="question text"
        value={state.text}
        onChange={handleQuestionTextChange}
        fullWidth
      />
      {
        !sort && (
          <Box aria-label="edit-container">
            {
              state.payload && (
                <Box>
                  {
                    state.payload.map((e, i) => (
                      <Box key={e.id} display="flex" sx={{ pl: 2 }}>
                        <TextField
                          variant="standard"
                          size="small"
                          label={`Option ${i + 1}`}
                          value={e.text}
                          onChange={
                            (evt: React.ChangeEvent<HTMLInputElement>) => {
                              handleEditOption(evt.target.value, e);
                            }
                          }
                          fullWidth
                        />
                        <IconButton
                          aria-label={`delete-option ${i + 1}`}
                          onClick={() => { handleDeleteOption(e); }}
                          size="large"
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    ))
                  }
                </Box>
              )
            }
            {
              state.text && (
                <Box display="flex" sx={{ pl: 2 }}>
                  <TextField
                    variant="standard"
                    size="small"
                    label="Add Option"
                    value={optionText}
                    onChange={onChangeOptionHandler}
                    fullWidth
                    onKeyPress={onEnterAddOption}
                  />
                  <IconButton aria-label="add-option" onClick={handleAddOption} size="large">
                    <AddIcon />
                  </IconButton>
                </Box>
              )
            }
          </Box>
        )
      }
      {
          !!sort && sortOptionList && (
            <Box aria-label="sort-container">
              <SortableList<SortOption>
                droppableId="sortable-options"
                list={sortOptionList}
                onDragEndHandler={onDragHandler}
              />
            </Box>
          )
      }
      {
        !disableHideAggregation && (
          <FormControlLabel
            label={<FormattedMessage id="questionEditor.hideAggregation" />}
            control={<Checkbox />}
            onChange={onHideFromAggregation}
            checked={state.hideAggregation || false}
          />
        )
      }
      {
        state.payload && state.payload.length > 0 && !sort && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }} gap={1}>
            <Button
              aria-label="sort-options"
              onClick={sortHandler}
              variant="outlined"
              size="small"
            >
              Reorder
            </Button>
            <Button
              aria-label="save-question"
              onClick={saveMultiQuestion}
              variant="outlined"
              size="small"
            >
              Save
            </Button>
            {
              question && (
                <Button
                  aria-label="cancel"
                  color="secondary"
                  onClick={cancelEdit}
                  variant="outlined"
                  size="small"
                >
                  Cancel
                </Button>
              )
            }
          </Box>
        )
      }
      {
        sort && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }} gap={2}>
            <Button
              aria-label="sort-options"
              onClick={saveSortHandler}
              variant="outlined"
            >
              Done
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

export default ChoiceQuestionEditor;
